/**
 * InstallAppButton — wraps the browser's `beforeinstallprompt` event
 * to give the user a one-click "Install Lumfi" CTA.
 *
 * Browser support matrix:
 *   • Android Chrome / Edge / Samsung Internet → fires
 *     `beforeinstallprompt`. We stash it and re-fire on click.
 *   • Desktop Chrome / Edge → same event.
 *   • iOS Safari → DOES NOT fire the event (Apple restriction).
 *     We detect iOS and show inline instructions instead
 *     (Share → "Add to Home Screen").
 *   • Already installed → window matchMedia('(display-mode: standalone)')
 *     returns true; we hide the button entirely.
 *
 * Registers the service worker on mount (idempotent — the browser
 * dedupes a re-register of the same scope).
 */
import { useEffect, useState } from "react";
import { Button } from "../ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../ui/dialog";
import { Download, Share, Plus } from "lucide-react";
import { useLang } from "../../contexts/LangContext";

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator?.standalone === true;
}

export function InstallAppButton({ variant = "default", size = "sm", className = "" }) {
  const { lang } = useLang();
  const isEs = lang === "es";
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone());
  const [iosOpen, setIosOpen] = useState(false);

  useEffect(() => {
    // Register SW once per session. The browser is idempotent.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.register("/sw.js").catch(() => {
        /* silent — SW failure shouldn't crash the app */
      });
    }
    const onPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    // iOS path: no native prompt available — show instructions.
    if (isIos() && !deferredPrompt) {
      setIosOpen(true);
      return;
    }
    if (!deferredPrompt) {
      // No prompt fired yet (e.g. desktop Firefox, in-app browsers).
      // We still open the iOS-style dialog because it explains
      // the manual install path for these contexts.
      setIosOpen(true);
      return;
    }
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    if (choice.outcome === "accepted") {
      setInstalled(true);
    }
    setDeferredPrompt(null);
  };

  return (
    <>
      <Button
        type="button"
        size={size}
        variant={variant}
        onClick={handleClick}
        data-testid="install-app-btn"
        className={className}
      >
        <Download className="h-3.5 w-3.5 mr-1.5" />
        {isEs ? "Instalar app" : "Install app"}
      </Button>

      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent className="max-w-md" data-testid="install-ios-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-4 w-4 text-emerald-600" />
              {isEs ? "Instalar Lumfi en tu celular" : "Install Lumfi on your phone"}
            </DialogTitle>
            <DialogDescription>
              {isEs
                ? "Suma Lumfi a tu pantalla de inicio para abrirlo como una app nativa, sin pasar por el navegador."
                : "Add Lumfi to your home screen so it opens like a native app, without going through the browser."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            {isIos() ? (
              <>
                <p className="text-slate-500">
                  {isEs
                    ? "Tu iPhone/iPad necesita un par de pasos manuales:"
                    : "Your iPhone/iPad needs a couple of manual steps:"}
                </p>
                <ol className="list-decimal pl-5 space-y-2">
                  <li>
                    {isEs ? "Tocá el botón " : "Tap the "}
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 border">
                      <Share className="h-3 w-3" /> {isEs ? "Compartir" : "Share"}
                    </span>
                    {isEs ? " en la barra inferior de Safari." : " button in Safari's bottom bar."}
                  </li>
                  <li>
                    {isEs ? "Baja y elige " : "Scroll down and pick "}
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 border">
                      <Plus className="h-3 w-3" /> {isEs ? "Agregar a inicio" : "Add to Home Screen"}
                    </span>.
                  </li>
                  <li>
                    {isEs
                      ? "Confirma el nombre (Lumfi) y listo — vas a tener un ícono directo en tu pantalla."
                      : "Confirm the name (Lumfi) and you're done — a direct icon will appear on your home screen."}
                  </li>
                </ol>
              </>
            ) : (
              <>
                <p>
                  {isEs
                    ? "Tu navegador todavía no muestra el botón nativo de instalación."
                    : "Your browser doesn't expose the native install prompt yet."}
                </p>
                <ul className="list-disc pl-5 space-y-1 text-slate-600 text-[13px]">
                  <li>
                    {isEs
                      ? "Abre esta página en Chrome o Edge desde tu celular Android."
                      : "Open this page in Chrome or Edge on Android."}
                  </li>
                  <li>
                    {isEs
                      ? "En desktop Chrome, mira el ícono ⊕ a la derecha de la barra de direcciones."
                      : "On desktop Chrome, look for the ⊕ icon at the right of the address bar."}
                  </li>
                </ul>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
